/**
 * Accueil connecté — BT-GSD-KARATE-ONBOARDING-NAV-001
 * Première visite / reprise Formation.
 */

const STORAGE_PREFIX = "shotokan-accueil-visited:";

/** @param {string|null|undefined} identity email ou username */
export function hasSeenWelcomeHome(identity) {
  if (!identity || typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(`${STORAGE_PREFIX}${String(identity).toLowerCase()}`) === "1";
  } catch {
    return false;
  }
}

/** @param {string|null|undefined} identity */
export function markWelcomeHomeSeen(identity) {
  if (!identity || typeof window === "undefined") return;
  try {
    window.localStorage.setItem(`${STORAGE_PREFIX}${String(identity).toLowerCase()}`, "1");
  } catch {
    /* ignore */
  }
}

/**
 * @param {'fr'|'en'} locale
 * @param {'hanshi'|'admin'|'sensei'|'assistant'|'parent'|'student'|string} role
 */
export function getWelcomeHomeCopy(locale = "fr", role = "hanshi") {
  const en = locale === "en";
  const isSensei = role === "sensei";
  const isStudent = role === "student";
  const isParent = role === "parent";
  const isAssistant = role === "assistant";

  let roleIntro;
  if (isSensei) {
    roleIntro = en
      ? "Find your classes, your students and their progression."
      : "Retrouvez vos cours, vos élèves et leur progression.";
  } else if (isStudent) {
    roleIntro = en
      ? "View your classes, your progression and your next steps."
      : "Consultez vos cours, votre progression et vos prochaines étapes.";
  } else if (isParent) {
    roleIntro = en
      ? "Follow your children's classes, attendance and progression."
      : "Suivez les cours, les présences et la progression de vos enfants.";
  } else if (isAssistant) {
    roleIntro = en
      ? "Support classes and stay aligned with your Sensei."
      : "Soutenez les cours et restez aligné avec votre Sensei.";
  } else {
    roleIntro = en
      ? "Manage the organisation and learn the main administration features."
      : "Gérez l'organisation et apprenez les principales fonctions d'administration.";
  }

  return {
    pageTitle: en ? "Home" : "Accueil",
    welcomeTitle: en ? "Welcome to Shotokan AAMC" : "Bienvenue dans Shotokan AAMC",
    welcomeBody: en
      ? "This space lets you manage members, classes, attendance, progression, grades and all important information for your organisation."
      : "Cet espace vous permet de gérer vos membres, vos cours, les présences, la progression, les grades et toutes les informations importantes de votre organisation.",
    roleIntro,
    firstTimeTitle: en ? "Using Shotokan AAMC for the first time?" : "Vous utilisez Shotokan AAMC pour la première fois ?",
    firstTimeBody: en
      ? "We prepared a guided training to show you the software step by step. You learn inside the app, with concrete examples and a path adapted to your role."
      : "Nous avons préparé une formation guidée pour vous montrer le logiciel étape par étape. Vous apprendrez directement dans l'application, avec des exemples concrets et un parcours adapté à votre rôle.",
    firstVisitBadge: en ? "First visit" : "Première visite",
    firstVisitBody: en
      ? "We recommend starting with Training. It will help you understand the system quickly before using management features."
      : "Nous vous recommandons de commencer par la Formation. Elle vous permettra de comprendre le système rapidement avant d'utiliser les fonctions de gestion.",
    startTraining: en ? "Start my training" : "Commencer ma formation",
    continueTraining: en ? "Continue my training" : "Continuer ma formation",
    exploreApp: en ? "Explore the application" : "Explorer l'application",
    resumeTitle: en ? "Resume my training" : "Reprendre ma formation",
    completedTitle: en ? "Training completed ✓" : "Formation terminée ✓",
    reviewTraining: en ? "Review training" : "Revoir la formation",
    learnCardTitle: en ? "Learn Shotokan AAMC" : "Apprendre Shotokan AAMC",
    learnCardBody: en
      ? "Training guides you from the dashboard to a full day in Shotokan AAMC."
      : "La formation vous accompagne du tableau de bord jusqu'à une journée complète dans Shotokan AAMC.",
    stepsLabelAdmin: en ? "10 steps — Administrator" : "10 étapes — Administrateur",
    stepsLabelSensei: en ? "8 steps — Sensei" : "8 étapes — Sensei",
    progressLabel: (done, total) =>
      en ? `${done} / ${total} steps completed` : `${done} / ${total} étapes terminées`,
    lastStepLabel: en ? "Last step" : "Dernière étape",
    howTitle: en ? "How will we learn the software?" : "Comment allons-nous apprendre le logiciel ?",
    howSteps: en
      ? [
          { n: 1, title: "I discover", body: "We show you the screen and its purpose." },
          { n: 2, title: "I understand", body: "A short explanation tells you what to look at." },
          { n: 3, title: "I do", body: "You perform the action yourself in the application." },
          { n: 4, title: "I continue", body: "Your progress is saved automatically and you can resume later." },
        ]
      : [
          { n: 1, title: "Je découvre", body: "On vous montre l'écran et son utilité." },
          { n: 2, title: "Je comprends", body: "Une courte explication vous indique ce qu'il faut regarder." },
          { n: 3, title: "Je fais", body: "Vous effectuez vous-même l'action dans l'application." },
          { n: 4, title: "Je continue", body: "Votre progression est enregistrée automatiquement et vous pouvez reprendre plus tard." },
        ],
    pathTitle: en ? "Your training path" : "Votre parcours de formation",
    viewFullTraining: en ? "View my full training" : "Voir toute ma formation",
    noLiveTraining: en
      ? "Guided training for your role will be available soon."
      : "La formation guidée pour votre rôle sera bientôt disponible.",
    openDashboard: en ? "Open dashboard" : "Ouvrir le tableau de bord",
  };
}

/** Aperçu compact des étapes Accueil (Admin 10 / Sensei 8) — titres courts. */
export function getWelcomePathPreview(trackId, locale = "fr") {
  const en = locale === "en";
  if (trackId === "sensei") {
    return en
      ? [
          "Dashboard",
          "Classes",
          "My students",
          "Attendance",
          "Progression",
          "Next grade",
          "Documents",
          "Full workday",
        ]
      : [
          "Tableau de bord",
          "Cours",
          "Mes élèves",
          "Présences",
          "Progression",
          "Prochain grade",
          "Documents",
          "Journée complète",
        ];
  }
  return en
    ? [
        "Dashboard",
        "Members and families",
        "Instructors and roles",
        "Accounts and access",
        "Dojos, classes and groups",
        "Attendance",
        "Progression",
        "Exams and validations",
        "Documents and evidence",
        "Full workday",
      ]
    : [
        "Tableau de bord",
        "Membres et familles",
        "Instructeurs et rôles",
        "Comptes et accès",
        "Dojos, cours et groupes",
        "Présences",
        "Progression",
        "Examens et validations",
        "Documents et preuves",
        "Faire une journée complète",
      ];
}
